import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import './Modal.css'; // Assuming a generic modal CSS

interface ReauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>; // Callback after successful re-auth
  providerId: string | undefined; // To handle different providers if needed
}

const ReauthModal: React.FC<ReauthModalProps> = ({ isOpen, onClose, onSuccess, providerId }) => {
  const { currentUser } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleReauthenticate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!currentUser || !currentUser.email) {
      setError('Could not find current user or email.');
      setLoading(false);
      return;
    }

    // Only handle password provider for now
    if (providerId !== 'password') {
        // For providers like Google, re-auth might happen differently or automatically.
        // For simplicity, assume success if not password provider, 
        // or add specific logic for other providers (e.g., reauthenticateWithPopup).
        console.log('Re-authentication not needed or handled differently for provider:', providerId);
        try {
            await onSuccess(); // Assume success and trigger deletion
        } catch (apiError: any) {
             setError(apiError.message || 'Failed to delete account after re-auth attempt.');
        } finally {
            setLoading(false);
            // Don't close immediately on error, let user see message
        }
        return; // Exit if not password provider
    }

    // Handle password re-authentication
    const credential = EmailAuthProvider.credential(currentUser.email, password);

    try {
      await reauthenticateWithCredential(currentUser, credential);
      console.log('Re-authentication successful');
      await onSuccess(); // Call the success callback (which will trigger deletion)
      onClose(); // Close modal on final success
    } catch (err: any) {
      console.error('Re-authentication failed:', err);
      // Provide user-friendly errors
      if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Re-authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={onClose}>&times;</button>
        <h2>Confirm Account Deletion</h2>
        <p>For security, please re-enter your password to confirm you want to permanently delete your account.</p>
        
        {providerId === 'password' ? (
            <form onSubmit={handleReauthenticate}>
            <div className="form-group">
                <label htmlFor="reauth-password">Password</label>
                <input
                type="password"
                id="reauth-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-control"
                autoFocus
                />
            </div>
            {error && <p className="error-message">{error}</p>}
            <button type="submit" className="btn btn-danger" disabled={loading}>
                {loading ? 'Confirming...' : 'Confirm and Delete Account'}
            </button>
            </form>
        ) : (
             // Handle non-password providers (e.g., Google, Apple)
             // Often, re-auth is handled via popup or is implicit if session is fresh.
             // For simplicity, we directly attempt the sensitive operation.
             <div className="non-password-reauth">
                 <p>Confirming your identity...</p> 
                 {/* We could potentially show a button here that triggers onSuccess directly,
                     or maybe reauthenticateWithPopup for social providers if needed. 
                     For now, the logic above handles calling onSuccess for non-password. */}
                 {error && <p className="error-message">{error}</p>}
                 <button onClick={onSuccess} className="btn btn-danger" disabled={loading}>
                    {loading ? 'Deleting...' : 'Confirm Deletion'}
                 </button>
             </div>
        )}

      </div>
    </div>
  );
};

export default ReauthModal; 